import React from 'react';
import {
  Grid,
  Card,
  Typography,
  Chip,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  useTheme,
  Paper,
  Box,
  Collapse,
  IconButton,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import TimelineIcon from '@mui/icons-material/Timeline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { DeliveryState, PaymentState } from '../../../enums/orders';
import {
  OrderEvent,
  OrderType,
  StateStamp,
} from '../../../interfaces/delivery-company/orders';
import {
  parseOrderEventActor,
  parseOrderEventChanges,
} from '../../../utils/orderevent';
import { EnhancedEventsTimeline } from '../../common/order-page-utils/order-events';
import {
  IDeliveryBatch,
  IOrderDeliveryTask,
  IPickupTask,
} from '../../../interfaces/delivery-company/tasks';
import { TaskHistoryCard } from '../../common/order-page-utils/task_history';

interface TimelineCardProps {
  title: string;
  icon: React.ReactNode;
  color: string;
  children: React.ReactNode;
}
export const TimelineCard: React.FC<TimelineCardProps> = ({
  title,
  icon,
  color,
  children,
}) => {
  const theme = useTheme();

  return (
    <Paper
      elevation={3}
      sx={{
        borderRadius: theme.shape.borderRadius,
        overflow: 'hidden',
        mb: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1, // Reduced from gap: 2
          p: 1, // Reduced from p: 2
          backgroundColor: color,
          color: theme.palette.getContrastText(color),
        }}
      >
        {React.cloneElement(icon as React.ReactElement, {
          sx: { fontSize: '1.25rem' }, // Make icon slightly smaller
        })}
        <Typography variant="subtitle1" fontWeight="bold">
          {' '}
          {/* Changed from h6 to subtitle1 */}
          {title}
        </Typography>
      </Box>
      <Box sx={{ p: 2, maxHeight: 500 }}>{children}</Box>
    </Paper>
  );
};

interface StateHistoryItemProps {
  stateItem: StateStamp;
  showDeliveryState: (state: DeliveryState) => React.ReactNode;
  showPaymentState: (state: PaymentState) => React.ReactNode;
}

const StateHistoryItem: React.FC<StateHistoryItemProps> = ({
  stateItem,
  showDeliveryState,
  showPaymentState,
}) => {
  const theme = useTheme();

  return (
    <ListItem
      sx={{
        alignItems: 'flex-start',
        px: 0,
        py: 2,
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <ListItemAvatar sx={{ minWidth: 48 }}>
        <Avatar
          sx={{
            bgcolor: theme.palette.grey[200],
            color: theme.palette.text.primary,
          }}
        >
          <HistoryIcon />
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={
          <Typography
            variant="body2"
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
            color="text.secondary"
          >
            {new Date(stateItem.timestamp).toLocaleString()}
            {showDeliveryState(stateItem.delivery_state)}
            {showPaymentState(stateItem.payment_state)}
          </Typography>
        }
      />
    </ListItem>
  );
};

interface OrderTimelineProps {
  statestamps?: StateStamp[];
  events?: OrderEvent[];
  color: string;
  showDeliveryState: (state: DeliveryState) => React.ReactNode;
  showPaymentState: (state: PaymentState) => React.ReactNode;
  parseOrderEventActor: (event: OrderEvent) => string;
  parseOrderEventChanges: (event: OrderEvent) => React.ReactNode[];
  pickup_tasks: IPickupTask[];
  delivery_tasks: IOrderDeliveryTask[];
  order: OrderType
}

const OrderTimeline: React.FC<OrderTimelineProps> = ({
  statestamps,
  events,
  color,
  showDeliveryState,
  showPaymentState,
  pickup_tasks,
  delivery_tasks,
  order,
}) => {
  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <TaskHistoryCard
          order={order}
          pickupTasks={pickup_tasks}
          deliveryTasks={delivery_tasks}
          color={color}
          updateDeliveryTask={(t) => {/* merchant can not update commission */}}
        />
      </Grid>

      {/* State History */}
      <Grid item xs={12} md={6}>
        <TimelineCard
          title="State History"
          icon={<HistoryIcon />}
          color={color}
        >
          <Box sx={{ p: 1, maxHeight: 500, overflow: 'auto' }}>
            <List sx={{ py: 0 }}>
              {statestamps?.map((stateItem, index) => (
                <React.Fragment key={stateItem.timestamp}>
                  <StateHistoryItem
                    stateItem={stateItem}
                    showDeliveryState={showDeliveryState}
                    showPaymentState={showPaymentState}
                  />
                  {index < statestamps.length - 1 && (
                    <Divider variant="inset" component="li" />
                  )}
                </React.Fragment>
              ))}
            </List>
          </Box>
        </TimelineCard>
      </Grid>

      {/* Events */}
      <Grid item xs={12} md={6}>
        <EnhancedEventsTimeline color={color} events={events} />
      </Grid>
    </Grid>
  );
};

export default OrderTimeline;
